/**
 * Communications Landing Page
 *
 * Main landing page for the Communications management system.
 * Features: 4 communication channels with comprehensive management capabilities
 */

import { useNavigate } from 'react-router-dom';
import {
  IconMail,
  IconMessage,
  IconBrandWhatsapp,
  IconBell,
  IconSend,
  IconChevronRight,
} from '@tabler/icons-react';
import { Stack, Group, Text, Title } from '../../../components/ui';
import { LayoutContainer } from '../../../components/ui/layout';
import { Card } from '../../../components/ui/data-display';
import { FadeIn, SlideIn } from '../../../components/animations/transitions';

export default function CommunicationsPage() {
  const navigate = useNavigate();

  return (
    <LayoutContainer py="xl">
      <FadeIn delay={100}>
        <Stack gap="xl">
          {/* HEADER */}
          <Stack gap="xs">
            <Group gap="sm">
              <IconSend size={32} strokeWidth={1.5} />
              <Title order={2}>Communications</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Manage all communication channels from a centralized dashboard
            </Text>
          </Stack>

          {/* CHANNEL BLOCKS */}
          <SlideIn direction="up" delay={150}>
            <Stack gap="md">
              {/* EMAIL */}
              <Card
                withBorder
                padding="lg"
                onClick={() => navigate('/admin/communications/email')}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="hover-card"
              >
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: 'rgba(66, 99, 235, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconMail size={24} stroke={1.5} style={{ color: '#4263eb' }} />
                    </div>
                    <div>
                      <Title order={4} fw={600}>Email</Title>
                      <Text size="sm" c="dimmed">Rich HTML email management with templates and delivery tracking</Text>
                    </div>
                  </Group>
                  <IconChevronRight size={20} style={{ opacity: 0.5 }} />
                </Group>
              </Card>

              {/* SMS */}
              <Card
                withBorder
                padding="lg"
                onClick={() => navigate('/admin/communications/sms')}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="hover-card"
              >
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: 'rgba(174, 62, 201, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconMessage size={24} stroke={1.5} style={{ color: '#ae3ec9' }} />
                    </div>
                    <div>
                      <Title order={4} fw={600}>SMS</Title>
                      <Text size="sm" c="dimmed">SMS messaging with character counting and segmentation</Text>
                    </div>
                  </Group>
                  <IconChevronRight size={20} style={{ opacity: 0.5 }} />
                </Group>
              </Card>

              {/* WHATSAPP */}
              <Card
                withBorder
                padding="lg"
                onClick={() => navigate('/admin/communications/whatsapp')}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="hover-card"
              >
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: 'rgba(18, 184, 134, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconBrandWhatsapp size={24} stroke={1.5} style={{ color: '#12b886' }} />
                    </div>
                    <div>
                      <Title order={4} fw={600}>WhatsApp</Title>
                      <Text size="sm" c="dimmed">Advanced messaging with media library and parameters</Text>
                    </div>
                  </Group>
                  <IconChevronRight size={20} style={{ opacity: 0.5 }} />
                </Group>
              </Card>

              {/* PUSH NOTIFICATION */}
              <Card
                withBorder
                padding="lg"
                onClick={() => navigate('/admin/communications/push')}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="hover-card"
              >
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: 'rgba(34, 139, 230, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconBell size={24} stroke={1.5} style={{ color: '#228be6' }} />
                    </div>
                    <div>
                      <Title order={4} fw={600}>Push Notifications</Title>
                      <Text size="sm" c="dimmed">Rich push notifications with multi-target support</Text>
                    </div>
                  </Group>
                  <IconChevronRight size={20} style={{ opacity: 0.5 }} />
                </Group>
              </Card>
            </Stack>
          </SlideIn>

          {/* FEATURES INFO */}
          <SlideIn direction="up" delay={200}>
            <Card withBorder padding="lg">
              <Stack gap="sm">
                <Title order={5} fw={600}>Common Features</Title>
                <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                  All channels include queue management, template creation, test interface,
                  priority-based delivery, scheduled messaging, retry mechanism, bulk operations,
                  and comprehensive analytics.
                </Text>
              </Stack>
            </Card>
          </SlideIn>
        </Stack>
      </FadeIn>

      <style>{`
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
        }
      `}</style>
    </LayoutContainer>
  );
}
